import qs, { type StringifiableRecord } from 'query-string';
import type { TableView } from 'types/views';

/**
 * Picks which view to land on after the view list loads: the current view if
 * it still exists, otherwise the favorite, otherwise the default.
 */
export const resolveViewToNavigateTo = (views: TableView[], currentViewId: string): string => {
  const allViewIds = views.map(view => view.id);
  if (currentViewId !== '' && allViewIds.includes(currentViewId)) return currentViewId;

  const favoriteViewId = views.find(view => view.is_favorite)?.id;
  if (favoriteViewId) return favoriteViewId;

  // The backend normally marks one default; fall back to the first view (or '')
  // rather than crashing the table if it ever doesn't.
  return views.find(view => view.is_default)?.id ?? views[0]?.id ?? '';
};

type ViewNavigationUrlOptions = {
  pathname: string;
  search: string;
  viewFilters: Record<string, unknown>;
  viewId: string;
};

export const buildViewNavigationUrl = ({
  pathname,
  search,
  viewFilters,
  viewId,
}: ViewNavigationUrlOptions): string => {
  // Current search params win over the view's own filters.
  const urlFilters = {
    ...viewFilters,
    ...Object.fromEntries(new URLSearchParams(search)),
  };

  // query-string url-encodes values and drops the empty trailing separator the
  // old hand-rolled reduce left behind. `urlFilters` is an untyped URL bag.
  return `${pathname}?${qs.stringify(urlFilters as StringifiableRecord, { arrayFormat: 'comma' })}#${viewId}`;
};
